import React, { useEffect, useRef, useState } from 'react';

/**
 * Calendar-style date picker — visually identical to the one in
 * InvestmentTracker (which itself mirrors Loans). Used wherever the
 * Business Tracker forms ask for a date so the picker UI is consistent
 * across modules.
 *
 * Props:
 *   value:       'YYYY-MM-DD' | ''  (controlled)
 *   onChange:    (iso) => void
 *   placeholder: shown when value is empty
 */

const FONT = "'DM Sans', sans-serif";
const C = {
    navyDark: '#1E293B',
    primaryGreen: '#2D5A3D',
    border: '#E5E7EB',
    muted: '#64748B',
    white: '#FFFFFF',
};

const DP_WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DP_MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const parseISO = (iso) => {
    if (!iso) return null;
    const parts = String(iso).split('-');
    if (parts.length !== 3) return null;
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    return Number.isNaN(d.getTime()) ? null : d;
};

const toISO = (d) => {
    const yr = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    return `${yr}-${mo}-${da}`;
};

const sameDay = (a, b) =>
    a && b
    && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();

function InlineDatePicker({ value, onChange, placeholder }) {
    const [open, setOpen] = useState(false);
    const [cursor, setCursor] = useState(() => {
        const init = parseISO(value) || new Date();
        return new Date(init.getFullYear(), init.getMonth(), 1);
    });
    const rootRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const openPicker = () => {
        const init = parseISO(value) || new Date();
        setCursor(new Date(init.getFullYear(), init.getMonth(), 1));
        setOpen(true);
    };
    const prevMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1));
    const nextMonth = () => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1));
    const selectDay = (day) => {
        const d = new Date(cursor.getFullYear(), cursor.getMonth(), day);
        onChange(toISO(d));
        setOpen(false);
    };
    const selectToday = () => {
        const today = new Date();
        onChange(toISO(today));
        setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
        setOpen(false);
    };

    const selected = parseISO(value);
    const today = new Date();
    const firstWeekday = new Date(cursor.getFullYear(), cursor.getMonth(), 1).getDay();
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    const displayValue = selected
        ? selected.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        : '';

    return (
        <div ref={rootRef} style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={() => { if (open) setOpen(false); else openPicker(); }}
                style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: 15,
                    fontFamily: FONT,
                    textAlign: 'left',
                    border: `2px solid ${open ? C.primaryGreen : C.border}`,
                    borderRadius: 12,
                    background: C.white,
                    color: displayValue ? C.navyDark : C.muted,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'border-color 0.15s ease',
                    boxSizing: 'border-box',
                }}
            >
                <span>{displayValue || placeholder || 'Select date'}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4 M8 2v4 M3 10h18" />
                </svg>
            </button>
            {open && (
                <div style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: 0,
                    right: 0,
                    background: C.white,
                    borderRadius: 16,
                    boxShadow: '0 16px 40px rgba(15, 23, 42, 0.22)',
                    border: `1px solid ${C.border}`,
                    padding: 16,
                    zIndex: 9100,
                    fontFamily: FONT,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <button type="button" onClick={prevMonth} style={navBtnStyle()} aria-label="Previous month">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 18l-6-6 6-6" />
                            </svg>
                        </button>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.navyDark }}>
                            {DP_MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
                        </div>
                        <button type="button" onClick={nextMonth} style={navBtnStyle()} aria-label="Next month">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M9 18l6-6-6-6" />
                            </svg>
                        </button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 6 }}>
                        {DP_WEEKDAYS.map((w, i) => (
                            <div key={`${w}-${i}`} style={{
                                textAlign: 'center',
                                fontSize: 11,
                                fontWeight: 700,
                                color: C.muted,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                padding: '4px 0',
                            }}>
                                {w}
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                        {cells.map((day, idx) => {
                            if (day === null) return <div key={`b-${idx}`} />;
                            const cellDate = new Date(cursor.getFullYear(), cursor.getMonth(), day);
                            const isSelected = sameDay(cellDate, selected);
                            const isToday = sameDay(cellDate, today);
                            let bg = 'transparent';
                            let color = C.navyDark;
                            let fontWeight = 500;
                            let border = '1px solid transparent';
                            if (isSelected) {
                                bg = C.primaryGreen;
                                color = C.white;
                                fontWeight = 700;
                            } else if (isToday) {
                                border = `1px solid ${C.primaryGreen}`;
                                color = C.primaryGreen;
                                fontWeight = 700;
                            }
                            return (
                                <button
                                    key={day}
                                    type="button"
                                    onClick={() => selectDay(day)}
                                    style={{
                                        background: bg,
                                        color,
                                        fontWeight,
                                        border,
                                        borderRadius: 10,
                                        height: 36,
                                        cursor: 'pointer',
                                        fontFamily: FONT,
                                        fontSize: 14,
                                        transition: 'background 0.12s, color 0.12s',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) e.currentTarget.style.background = '#F0FDF4';
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSelected) e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    {day}
                                </button>
                            );
                        })}
                    </div>
                    <div style={{
                        marginTop: 12,
                        paddingTop: 12,
                        borderTop: `1px solid ${C.border}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}>
                        <button type="button" onClick={selectToday} style={{
                            background: 'transparent',
                            border: 'none',
                            color: C.primaryGreen,
                            fontFamily: FONT,
                            fontSize: 13,
                            fontWeight: 700,
                            cursor: 'pointer',
                            padding: '6px 8px',
                        }}>
                            Today
                        </button>
                        <button type="button" onClick={() => setOpen(false)} style={{
                            background: 'transparent',
                            border: 'none',
                            color: C.muted,
                            fontFamily: FONT,
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: 'pointer',
                            padding: '6px 8px',
                        }}>
                            Close
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function navBtnStyle() {
    return {
        background: '#F1F5F9',
        border: 'none',
        width: 32,
        height: 32,
        borderRadius: 8,
        cursor: 'pointer',
        color: C.navyDark,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
}

export default InlineDatePicker;
